import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, CreateUserRequest, Department, UpdateUserRequest } from '../types/index';
import apiService from '../services/api';
import PageHeader from './ui/PageHeader';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';
import EmptyState from './ui/EmptyState';
import { PlusIcon, UsersIcon } from '@heroicons/react/24/outline';

const Users: React.FC = () => {
  const { user: _currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [contractors, setContractors] = useState<{ id: string; name: string }[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'municipal_staff' as const,
    departmentId: '',
    address: '',
    wardNumber: '',
    technicianKind: 'in_house',
    contractorId: '',
  });
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'municipal_staff' as
      | 'municipal_staff'
      | 'department_head'
      | 'admin'
      | 'technician'
      | 'resident',
    departmentId: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchContractors();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getUsers();
      if (response.success && response.data) {
        // Filter out residents, only show municipal staff (and technicians,
        // whether in-house or working for a contractor)
        const staffOnly = response.data.filter((user: User) =>
          ['admin', 'department_head', 'municipal_staff', 'technician', 'field_worker'].includes(user.role)
        );
        setUsers(staffOnly);
      } else {
        throw new Error(response.error?.message || 'Failed to fetch users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContractors = async () => {
    try {
      const response = await apiService.get<{ id: string; name: string }[]>('/platform/contractors');
      if (response.success && response.data) setContractors(response.data);
    } catch (err) {
      console.error('Failed to fetch contractors:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiService.getDepartments();
      if (response.success && response.data) {
        setDepartments(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate department is selected for roles that require it
    if (shouldShowDepartmentField() && !formData.departmentId) {
      setError('Department is required for this role');
      return;
    }
    // Contractor technicians must be linked to an actual contractor company
    if (isTechnicianRole(formData.role) && formData.technicianKind === 'contractor' && !formData.contractorId) {
      setError('Select which contractor this technician works for');
      return;
    }

    try {
      const createUserData: CreateUserRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role as any,
        departmentId: formData.departmentId || undefined,
        ...(isTechnicianRole(formData.role) ? {
          technicianKind: formData.technicianKind,
          contractorId: formData.technicianKind === 'contractor' ? formData.contractorId : undefined,
        } : {}),
      };

      const response = await apiService.createUser(createUserData);

      if (response.success && response.data) {
        setShowCreateModal(false);
        setError(''); // Clear any errors
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          role: 'municipal_staff',
          departmentId: '',
          address: '',
          wardNumber: '',
          technicianKind: 'in_house',
          contractorId: '',
        });
        fetchUsers(); // Refresh the list
      } else {
        throw new Error(response.error?.message || 'Failed to create user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUser) return;
    
    // Validate department is selected for roles that require it
    if (isDepartmentRequiredForEdit(editFormData.role) && !editFormData.departmentId) {
      setError('Department is required for this role');
      return;
    }
    
    // Check for significant changes that might need confirmation
    const roleChanged = editingUser.role !== editFormData.role;
    const departmentChanged = editingUser.departmentId !== editFormData.departmentId;
    
    if (roleChanged || departmentChanged) {
      let message = '';
      if (roleChanged && departmentChanged) {
        message = `Are you sure you want to change ${editingUser.firstName}'s role from "${getRoleDisplayName(editingUser.role)}" to "${getRoleDisplayName(editFormData.role)}" and reassign them from "${getDepartmentName(editingUser.departmentId)}" to "${getDepartmentName(editFormData.departmentId)}"?`;
      } else if (roleChanged) {
        message = `Are you sure you want to change ${editingUser.firstName}'s role from "${getRoleDisplayName(editingUser.role)}" to "${getRoleDisplayName(editFormData.role)}"?`;
      } else if (departmentChanged) {
        message = `Are you sure you want to reassign ${editingUser.firstName} from "${getDepartmentName(editingUser.departmentId)}" to "${getDepartmentName(editFormData.departmentId)}"?`;
      }
      
      if (message && !window.confirm(message)) {
        return;
      }
    }
    
    try {
      const updateUserData: UpdateUserRequest = {
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        phone: editFormData.phone,
        role: editFormData.role as any,
        departmentId: editFormData.departmentId || undefined,
      };

      const response = await apiService.updateUser(editingUser.id, updateUserData);
      
      if (response.success && response.data) {
        setShowEditModal(false);
        setEditingUser(null);
        setError(''); // Clear any errors
        fetchUsers(); // Refresh the list
      } else {
        throw new Error(response.error?.message || 'Failed to update user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone || '',
      role: user.role as
        | 'municipal_staff'
        | 'department_head'
        | 'admin'
        | 'technician'
        | 'resident',
      departmentId: user.departmentId || '',
    });
    setShowEditModal(true);
    setError(''); // Clear any previous errors
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditFormData({
      firstName: '',
      lastName: '',
      phone: '',
      role: 'municipal_staff',
      departmentId: '',
    });
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteUser = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const response = await apiService.deleteUser(id);
      if (response.success) {
        setUsers(prev => prev.filter(user => user.id !== id));
      } else {
        throw new Error(response.error?.message || 'Failed to delete user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const response = await apiService.toggleUserActive(id, !currentStatus);
      if (response.success && response.data) {
        setUsers(prev => prev.map(user => 
          user.id === id ? { ...user, isActive: !currentStatus } : user
        ));
      } else {
        throw new Error(response.error?.message || 'Failed to update user status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    }
  };

  const getRoleDisplayName = (role: string) => {
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getDepartmentName = (departmentId: string | undefined) => {
    if (!departmentId) return '-';
    const department = departments.find(d => d.id === departmentId);
    return department ? department.name : '-';
  };

  const isTechnicianRole = (role: string) => role === 'technician' || role === 'field_worker';

  // In-house technicians are matched to reports by department (see
  // DispatchService.resolveEligibleTechnicians) — one with no department
  // is invisible to auto-dispatch for every category. Contractor
  // technicians are matched via contractorId instead, so department
  // doesn't apply to them.
  const shouldShowDepartmentField = () => {
    return (
      formData.role === 'municipal_staff' ||
      formData.role === 'department_head' ||
      (isTechnicianRole(formData.role) && formData.technicianKind !== 'contractor')
    );
  };

  const shouldShowDepartmentFieldForRole = (role: string) => {
    return role === 'municipal_staff' || role === 'department_head' || isTechnicianRole(role);
  };

  // Mirrors shouldShowDepartmentField's contractor exemption, but reads
  // technicianKind from the user being edited rather than form state
  // (the edit form doesn't let you change technicianKind).
  const isDepartmentRequiredForEdit = (role: string) => {
    if (!shouldShowDepartmentFieldForRole(role)) return false;
    if (isTechnicianRole(role) && editingUser?.technicianKind === 'contractor') return false;
    return true;
  };

  const getDepartmentChangeImpact = (oldDepartmentId: string | undefined, newDepartmentId: string | undefined) => {
    if (!oldDepartmentId || !newDepartmentId || oldDepartmentId === newDepartmentId) {
      return null;
    }
    
    const oldDept = departments.find(d => d.id === oldDepartmentId);
    const newDept = departments.find(d => d.id === newDepartmentId);
    
    if (!oldDept || !newDept) return null;
    
    return {
      from: oldDept.name,
      to: newDept.name,
      message: `User will be moved from ${oldDept.name} to ${newDept.name}`
    };
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Staff"
        subtitle={`${users.length} municipal staff, department heads, and administrators`}
        actions={
          <Button
            variant="primary"
            icon={PlusIcon}
            onClick={() => {
              setShowCreateModal(true);
              setError('');
            }}
          >
            Add New User
          </Button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {users.length === 0 ? (
        <EmptyState icon={UsersIcon} title="No staff accounts yet" description="Add your first municipal staff member to get started." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100">
                          <span className="text-xs font-semibold text-primary-800">
                            {user.firstName.charAt(0)}
                            {user.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {user.firstName} {user.lastName}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{user.email}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{user.phone || '—'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{getRoleDisplayName(user.role)}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{getDepartmentName(user.departmentId)}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <Badge tone={user.isActive ? 'success' : 'danger'}>{user.isActive ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditModal(user)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant={user.isActive ? 'danger-ghost' : 'secondary'}
                          onClick={() => handleToggleActive(user.id, user.isActive)}
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button size="sm" variant="danger-ghost" onClick={() => handleDeleteUser(user.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New User"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="create-user-form" variant="primary">
              Create User
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateUser} id="create-user-form">
          <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="municipal_staff">Municipal Staff</option>
                      <option value="department_head">Department Head</option>
                      <option value="admin">Admin</option>
                      <option value="technician">Technician</option>
                      <option value="resident">Resident</option>
                    </select>
                  </div>
                  {isTechnicianRole(formData.role) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Technician type</label>
                        <select
                          name="technicianKind"
                          value={formData.technicianKind}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="in_house">Municipal staff (in-house)</option>
                          <option value="contractor">Service contractor</option>
                        </select>
                      </div>
                      {formData.technicianKind === 'contractor' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contractor</label>
                          <select
                            name="contractorId"
                            value={formData.contractorId}
                            onChange={handleInputChange}
                            required
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="">Select a contractor</option>
                            {contractors.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                          {contractors.length === 0 && (
                            <p className="mt-1 text-xs text-gray-500">
                              No contractors on file yet — add one from Contractor Performance first.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {shouldShowDepartmentField() && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                      <select
                        name="departmentId"
                        value={formData.departmentId}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select a department</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={showEditModal && !!editingUser}
        onClose={closeEditModal}
        title={editingUser ? `Edit User: ${editingUser.firstName} ${editingUser.lastName}` : 'Edit User'}
        footer={
          <>
            <Button variant="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button type="submit" form="edit-user-form" variant="primary">
              Update User
            </Button>
          </>
        }
      >
        {editingUser && (
          <>
            {/* Current User Info */}
            <div className="mb-4 rounded-md bg-gray-50 p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div><strong>Current Role:</strong> {getRoleDisplayName(editingUser.role)}</div>
                <div><strong>Current Department:</strong> {getDepartmentName(editingUser.departmentId)}</div>
                <div><strong>Email:</strong> {editingUser.email}</div>
              </div>
            </div>

            <form onSubmit={handleEditUser} id="edit-user-form">
              <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={editFormData.firstName}
                      onChange={handleEditInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={editFormData.lastName}
                      onChange={handleEditInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={editFormData.phone}
                      onChange={handleEditInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                    <select
                      name="role"
                      value={editFormData.role}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="municipal_staff">Municipal Staff</option>
                      <option value="department_head">Department Head</option>
                      <option value="admin">Admin</option>
                      <option value="technician">Technician</option>
                      <option value="resident">Resident</option>
                    </select>
                  </div>
                  {shouldShowDepartmentFieldForRole(editFormData.role) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                      <select
                        name="departmentId"
                        value={editFormData.departmentId}
                        onChange={handleEditInputChange}
                        required={isDepartmentRequiredForEdit(editFormData.role)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select a department</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                      {isTechnicianRole(editFormData.role) &&
                        editingUser?.technicianKind === 'contractor' && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Optional for contractor technicians — they're dispatched by
                            contractor, not department.
                          </p>
                        )}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Changing departments will affect report assignments and workload distribution.
                      </p>
                    </div>
                  )}
                  
                  {/* Role Change Warning */}
                  {editingUser.role !== editFormData.role && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="text-sm text-yellow-800">
                        <strong>⚠️ Role Change Warning:</strong> Changing user roles may affect their permissions and access levels.
                      </div>
                    </div>
                  )}
                  
                  {/* Department Change Warning */}
                  {(() => {
                    const impact = getDepartmentChangeImpact(editingUser.departmentId, editFormData.departmentId);
                    if (!impact) return null;
                    
                    return (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="text-sm text-blue-800">
                          <strong>🔄 Department Reassignment:</strong> {impact.message}
                          <br />
                          <span className="text-xs">
                            This may affect their current assignments and future report routing.
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Users;
